/**
 * C2 — the 3-state entitlement engine (mvp-scope-and-stack §4).
 *
 * Three states, never two: an honest "indeterminate" beats a confident wrong answer
 * when the directory data is missing, partial, or unrecognised.
 */
import type { SubscribedSku } from '@/graph/types';
import { ENTITLEMENT_CATALOG, type CapabilityDef } from './catalog';

export type EntitlementState = 'entitled' | 'not-entitled' | 'indeterminate';

export interface CapabilityEntitlement {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly state: EntitlementState;
  /** Human-readable reason for the state. */
  readonly detail: string;
  readonly prepaidUnits?: number;
  readonly consumedUnits?: number;
  readonly matchedSkuPartNumbers?: string[];
}

interface EnabledPlan {
  name: string;
  skuPartNumber: string;
  prepaid: number;
  consumed: number;
}

function extractEnabledPlans(skus: SubscribedSku[]): EnabledPlan[] {
  const plans: EnabledPlan[] = [];
  for (const sku of skus) {
    const prepaid = sku.prepaidUnits?.enabled ?? 0;
    const consumed = sku.consumedUnits ?? 0;
    const part = sku.skuPartNumber ?? 'UNKNOWN';
    for (const plan of sku.servicePlans ?? []) {
      // Only count plans that are actually provisioned/enabled at the tenant.
      const status = (plan.provisioningStatus ?? '').toLowerCase();
      if (status && status !== 'success' && status !== 'pendinginput') continue;
      plans.push({
        name: (plan.servicePlanName ?? '').toUpperCase(),
        skuPartNumber: part,
        prepaid,
        consumed,
      });
    }
  }
  return plans;
}

function matches(def: CapabilityDef, planName: string): boolean {
  return def.planNamePatterns.some((p) => planName.includes(p.toUpperCase()));
}

/**
 * @param skus  result of GET /subscribedSkus, or null if the read failed/was denied.
 */
export function evaluateEntitlements(
  skus: SubscribedSku[] | null,
): CapabilityEntitlement[] {
  // No data at all → everything is indeterminate (not "not-entitled").
  if (skus == null) {
    return ENTITLEMENT_CATALOG.map((def) => ({
      id: def.id,
      label: def.label,
      description: def.description,
      state: 'indeterminate' as const,
      detail: 'Directory/organisation data could not be read. Consent or connectivity issue.',
    }));
  }

  const enabledPlans = extractEnabledPlans(skus);

  return ENTITLEMENT_CATALOG.map((def) => {
    const hits = enabledPlans.filter((p) => matches(def, p.name));

    if (hits.length === 0) {
      return {
        id: def.id,
        label: def.label,
        description: def.description,
        state: 'not-entitled' as const,
        detail: 'No matching enabled service plan found in the tenant’s SKUs.',
      };
    }

    const skuParts = Array.from(new Set(hits.map((h) => h.skuPartNumber)));
    const prepaid = hits.reduce((m, h) => Math.max(m, h.prepaid), 0);
    const consumed = hits.reduce((m, h) => Math.max(m, h.consumed), 0);

    return {
      id: def.id,
      label: def.label,
      description: def.description,
      state: 'entitled' as const,
      detail:
        prepaid > 0
          ? `${consumed} of ${prepaid} seats assigned via ${skuParts.join(', ')}.`
          : `Enabled via ${skuParts.join(', ')}.`,
      prepaidUnits: prepaid,
      consumedUnits: consumed,
      matchedSkuPartNumbers: skuParts,
    };
  });
}
